import { BUTTON_SIZE, BUTTON_TYPE, COLORS, DSButton } from '@repo/design-system/index'
import { QIcon_close_default, QIcon_menu_button_hamburger } from '@repo/icons/index'
import { useEffect, useState } from 'react'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useHandleLogout from '../../../../../modules/hooks/student/auth/useHandleLogout'
import { n_ai_tutor, n_landing, s_menu } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import links from '../../../../../modules/links'
import HeaderNavigationBarV2Mobile from '../../header-navigation/mobile/HeaderNavigationBarV2'
import GuideLavLinks from '../MainNav/GuideNavLinks'
import LanguageSelector from '../MainNav/LanguageSelector'
import NavLogo from '../MainNav/NavLogo'
import ProductNavLinks from '../MainNav/ProductNavLinks'
import useLandingNav, { LANDING_NAV } from '../useLandingNav'
import SubNav from './SubNav'

const navLinks = [
  {
    title: n_landing.footer.title_2,
    link: '/student/landing/blog?type=blog#1',
  },
  {
    title: n_landing.gnb_menu_3.list,
    link: '/ko/student/landing/b2b',
  },
]

const MobileLandingNav = () => {
  const [isNavOpen, setIsNavOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const { isLogin, isAITutorMenu, navigateToSignIn, handleStartClick } = useLandingNav()
  const { t, isLocaleKO } = useTranslation()
  const handleLogout = useHandleLogout()
  const router = useAppRouter()

  const pageWithSubNav = ['pricing', 'challenge']
  const isSubNavVisible = pageWithSubNav.some((page) => router.pathname.includes(page))

  const authButton = {
    title: isLogin ? t(s_menu.mypage.sign_out) : t(s_menu.landing.signin),
    onClick: isLogin
      ? () => {
          setIsNavOpen(false)
          handleLogout()
        }
      : () => navigateToSignIn(),
  }

  const navCTA = {
    title: isLogin ? t(n_landing.mo_menu.btn_4) : t(n_landing.mo_menu.btn_2),
    onClick: isLogin ? () => window.open(links.aiTutor.download, '_blank', 'noopener,noreferrer') : handleStartClick,
  }

  const handleLinkClick = (link: string) => {
    router.push(link)
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 58)
    }
    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isNavOpen])

  if (!isLocaleKO) return <HeaderNavigationBarV2Mobile />

  return (
    <>
      <div
        className={`sticky top-0 z-[10000] backdrop-blur-sm transform transition-transform duration-300 ease-in-out ${
          isScrolled && isSubNavVisible ? '-translate-y-56' : 'translate-y-0'
        }`}
      >
        <header
          className={`${LANDING_NAV.HEIGHT} ${LANDING_NAV.Z_INDEX} p-20 w-full flex items-center justify-between border-b border-gray-100	bg-white/90`}
        >
          <div className="flex gap-x-8 items-center">
            <NavLogo />
            {isSubNavVisible && (
              <span className="text-purple-500 Body1-16Bd">
                {isAITutorMenu ? t(n_ai_tutor.menu.title) : t(n_landing.sub_menu_AItutor.list_2)}
              </span>
            )}
          </div>
          <div>
            <ul className="flex items-center gap-x-16">
              <div className="min-w-[44px]">
                <DSButton type={BUTTON_TYPE.SOLID} size={BUTTON_SIZE.SM} onClick={navCTA.onClick}>
                  <DSButton.Label customTxtColor="text-white">{navCTA.title}</DSButton.Label>
                </DSButton>
              </div>
              <button onClick={() => setIsNavOpen(true)}>
                <QIcon_menu_button_hamburger width={20} height={20} />
              </button>
            </ul>
          </div>
        </header>
        {isSubNavVisible && <SubNav />}
      </div>

      <div
        className={`fixed inset-0 h-dvh bg-white z-[999999] flex flex-col justify-between transform transition-transform duration-500 ease-in-out ${
          isNavOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col flex-1 overflow-y-auto">
          <header
            className={`${LANDING_NAV.Z_INDEX} p-20 sticky bg-white top-0 w-full flex items-center justify-between`}
          >
            <NavLogo />
            <button onClick={() => setIsNavOpen(false)}>
              <QIcon_close_default height={24} width={24} />
            </button>
          </header>

          <nav className="flex flex-col">
            <div className="px-20 py-16">
              <ProductNavLinks />
            </div>

            {navLinks.map((link, index) => (
              <button
                key={index}
                className="px-20 py-16 w-full flex items-center justify-between"
                onClick={() => handleLinkClick(link.link)}
              >
                <div className="flex items-center gap-x-8">
                  <span className="Body1-16Bd">{t(link.title)}</span>
                </div>
              </button>
            ))}
            <div className="px-20 py-16">
              <GuideLavLinks onNavBarClose={() => setIsNavOpen(false)} />
            </div>
            <div className="px-20 py-16">
              <LanguageSelector onFinish={() => setIsNavOpen(false)} />
            </div>
          </nav>
        </div>

        <section className="p-20 flex flex-col gap-y-10">
          <DSButton
            type={BUTTON_TYPE.OUTLINE}
            size={BUTTON_SIZE.SM}
            customBorderColor={COLORS.PURPLE500}
            onClick={authButton.onClick}
          >
            <DSButton.Label customTxtColor={COLORS.PURPLE500}>{t(authButton.title)}</DSButton.Label>
          </DSButton>
          <DSButton
            type={BUTTON_TYPE.SOLID}
            size={BUTTON_SIZE.MD}
            customBgColor={COLORS.PURPLE500}
            onClick={navCTA.onClick}
          >
            <DSButton.Label customTxtColor="text-white">{navCTA.title}</DSButton.Label>
          </DSButton>
        </section>
      </div>
    </>
  )
}

export default MobileLandingNav
